import { useCallback, useRef, useState } from 'react'

import { ApiError } from '@/api/client'

/**
 * Backend gates sensitive actions (team invite/activate/deactivate, ZIP
 * export, admin block/unblock/resolve) behind `password.confirm` middleware
 * (RequireRecentPasswordConfirmation), which returns 423 with
 * `{requiresPasswordConfirmation: true}` when the session's confirmation
 * has expired or never happened. This hook centralizes catching that 423,
 * opening a password-confirm modal, and retrying the original action once
 * confirmed — every mutation behind that middleware should route its
 * onError through `guardedError`.
 */
export function usePasswordConfirmGate() {
  const [show, setShow] = useState(false)
  const pendingRetryRef = useRef<(() => void) | null>(null)

  const guardedError = useCallback((error: unknown, retry: () => void): boolean => {
    if (error instanceof ApiError && error.status === 423) {
      pendingRetryRef.current = retry
      setShow(true)
      return true
    }
    return false
  }, [])

  const handleConfirmed = useCallback(() => {
    setShow(false)
    const retry = pendingRetryRef.current
    pendingRetryRef.current = null
    retry?.()
  }, [])

  return { show, setShow, guardedError, handleConfirmed }
}
